import { useState, type ReactNode } from "react";

import { AiNote } from "@/components/AiNote";
import { AlarmManager, type AlarmLevel } from "@/components/AlarmManager";
import { ChartCard } from "@/components/ChartCard";
import { DeepLink } from "@/components/DeepLink";
import { TeslimatChart } from "@/features/ozet/TeslimatHaftaModal";
import { EmptyState } from "@/components/EmptyState";
import { HealthRing } from "@/components/HealthRing";
import { KpiCard } from "@/components/KpiCard";
import { PeriodTabs, type Period } from "@/components/PeriodTabs";
import { SortableTable, type ColumnDef } from "@/components/SortableTable";
import {
  Button,
  ModalShell,
  Pill,
  type PillTone,
  Ribbon,
  type RibbonTone,
  SectionHeader,
  SegmentedTabs,
} from "@/components/ui";
import { VerdictBox } from "@/components/VerdictBox";
import { t } from "@/i18n";

const PILL_TONES: PillTone[] = ["ok", "warn", "bad", "info", "muted"];
const RIBBON_TONES: RibbonTone[] = ["ok", "warn", "bad", "info"];

interface DemoRow {
  id: number;
  varyant: string;
  adet: number;
}

const demoRows: DemoRow[] = [
  { id: 1, varyant: "Kırmızı - M", adet: 3 },
  { id: 2, varyant: "Mavi - S", adet: 14 },
  { id: 3, varyant: "Yeşil - L", adet: 0 },
];

const demoColumns: ColumnDef<DemoRow>[] = [
  { key: "varyant", header: "Varyant", accessor: (r) => r.varyant },
  { key: "adet", header: "Adet", accessor: (r) => r.adet, sortValue: (r) => r.adet },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-b border-line pb-8">
      <h2 className="text-sm font-bold uppercase tracking-wide text-muted">{title}</h2>
      {children}
    </section>
  );
}

/**
 * /dev/kit — gorsel denetim icin tum bilesenlerin ornekleri (Storybook YOK,
 * docs/faz5-arayuz-spec.md §5 P5-F1 notu). Uretim akisina dahil degildir.
 */
export function Kit() {
  const [period, setPeriod] = useState<Period>(7);
  const [alarmLevel, setAlarmLevel] = useState<AlarmLevel>("normal");
  const [segment, setSegment] = useState<"a" | "b" | "c">("a");
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-bold text-ink">{t("kit.title")}</h1>
        <p className="text-sm text-muted">{t("kit.subtitle")}</p>
      </header>

      <AlarmManager level={alarmLevel} criticalCount={alarmLevel === "critical" ? 3 : 0} />

      <Section title="SectionHeader">
        <SectionHeader
          title="Örnek bölüm"
          note="kısa açıklama"
          actions={
            <Button variant="outline" size="sm">
              Aksiyon
            </Button>
          }
        />
      </Section>

      <Section title="Pill tonları">
        <div className="flex flex-wrap gap-2">
          {PILL_TONES.map((tone) => (
            <Pill key={tone} tone={tone}>
              {tone}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Button varyantları">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary">Primary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="link">Link</Button>
          <Button variant="primary" size="sm">
            Primary sm
          </Button>
        </div>
      </Section>

      <Section title="SegmentedTabs">
        <SegmentedTabs
          ariaLabel="Kit örnek sekme"
          value={segment}
          onChange={setSegment}
          options={[
            { value: "a", label: "A" },
            { value: "b", label: "B" },
            { value: "c", label: "C" },
          ]}
        />
      </Section>

      <Section title="Ribbon">
        <div className="space-y-2">
          {RIBBON_TONES.map((tone) => (
            <Ribbon key={tone} tone={tone}>
              {tone} uyarı örneği metni.
            </Ribbon>
          ))}
        </div>
      </Section>

      <Section title="ModalShell">
        <Button variant="primary" onClick={() => setModalOpen(true)}>
          Modal aç
        </Button>
        <ModalShell open={modalOpen} title="Örnek modal" onClose={() => setModalOpen(false)}>
          <p className="text-sm text-ink">Modal gövde içeriği.</p>
        </ModalShell>
      </Section>

      <Section title="KpiCard — live / not_connected / not_supported">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiCard title="Bugünkü sipariş" state="live" value={128} onOpenDetail={() => {}} onAnalyze={() => {}} />
          <KpiCard title="Kritik stok" state="not_connected" reason="henuz_senkron_yok" setupHref="/kurulum" />
          <KpiCard title="Terk edilmiş sepet" state="not_supported" />
        </div>
      </Section>

      <Section title="HealthRing">
        <div className="flex gap-6">
          <HealthRing value={null} />
          <HealthRing value={42} />
          <HealthRing value={88} />
        </div>
      </Section>

      <Section title="VerdictBox">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <VerdictBox level="good" title="Kargolama hedefinde" detail="24 saat eşiğinin altında." />
          <VerdictBox level="warn" title="Destek yanıt süresi uzuyor" />
          <VerdictBox level="bad" title="Kritik stok eşiği aşıldı" />
        </div>
      </Section>

      <Section title="PeriodTabs">
        <PeriodTabs value={period} onChange={setPeriod} />
      </Section>

      <Section title="EmptyState">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <EmptyState kind="not_connected" reason="baglanti_eksik" setupHref="/kurulum" />
          <EmptyState kind="not_supported" />
        </div>
      </Section>

      <Section title="AiNote">
        <AiNote
          markdown={"**Öne çıkan**: iade oranı geçen haftaya göre arttı.\n\n- Kargo süresi stabil\n- Destek yanıtı yavaşladı"}
          followUps={["Neden arttı?", "Hangi ürünlerde?"]}
        />
      </Section>

      <Section title="SortableTable">
        <SortableTable columns={demoColumns} rows={demoRows} rowKey={(r) => r.id} />
      </Section>

      <Section title="DeepLink">
        <div className="flex gap-4 text-sm">
          <DeepLink href="https://example-admin.local/order/1" label="Sipariş #1 (Ticimax admin)" />
          <DeepLink href={null} label="Derin bağlantı yok" />
        </div>
      </Section>

      <Section title="AlarmManager">
        <div className="flex gap-2">
          {(["normal", "warning", "critical"] as AlarmLevel[]).map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => setAlarmLevel(level)}
              className={`min-h-[44px] rounded-md border border-line px-3 text-sm font-medium ${
                alarmLevel === level ? "bg-blue-pale text-brand-primary" : "text-muted"
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </Section>

      <Section title="ChartCard">
        <ChartCard
          title="Saatlik satış"
          type="line"
          labelEvery={3}
          data={{
            labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
            datasets: [{ label: "Satış", data: Array.from({ length: 24 }, () => Math.round(Math.random() * 100)) }],
          }}
        />
      </Section>

      <Section title="TeslimatChart (haftalık, tıklanır)">
        <TeslimatOrnek />
      </Section>
    </div>
  );
}

/** Dev kit ornegi — 16 sahte hafta; tiklanan hafta kartin altina yazilir. */
function TeslimatOrnek() {
  const [secilen, setSecilen] = useState<number | null>(null);
  return (
        <div className="card">
          <TeslimatChart
            haftalar={Array.from({ length: 16 }, (_, i) => {
              const baslangic = new Date(Date.UTC(2026, 5, 1 + i * 7));
              return {
                yil: "2026",
                hafta_no: 23 + i,
                hafta_baslangic: baslangic.toISOString().slice(0, 10),
                ort_saniye: Math.round((1.5 + Math.abs(Math.sin(i * 1.3)) * 2.7) * 86400),
                min_saniye: 86400,
                max_saniye: 5 * 86400,
                adet: 40 + i * 3,
              };
            })}
            onHaftaSec={(s) => setSecilen(s.hafta_no)}
          />
          <p className="mt-2 text-xs text-muted" data-testid="kit-teslimat-secim">
            {secilen == null ? "Bir haftaya tıklayın" : `Seçilen hafta: ${secilen}`}
          </p>
        </div>
  );
}
